import React, { useState, useEffect } from 'react';
import BlogCreativeCanvas from './BlogCreativeCanvas';
import BlogPostSelector from './BlogPostSelector';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import AICaptionPanel from './AICaptionPanel';
import { useToast } from '../../../hooks/use-toast';
import { exportUnifiedCanvas } from './unifiedExport';

// BlogSocialManager: hosts the BlogPostSelector and the dedicated canvas for blog creatives.
// Keeps the blog creative flow separate from the RSS/job flow and from UnifiedSocialCanvas.
export default function BlogSocialManager({ initialVariant = null, selectedBlog = null }) {
  const [currentVariant, setCurrentVariant] = useState(initialVariant);
  const [overrides, setOverrides] = useState({});
  const [variants, setVariants] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const result = await exportUnifiedCanvas('blog-creative-canvas');
      if (result.success) {
        toast({
          title: "Export Successful",
          description: "Blog creative exported successfully",
        });
      } else {
        toast({
          title: "Export Failed", 
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Export Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  function handleApply(variant) {
    setCurrentVariant(variant);
    setOverrides({});
  }

  function handleFieldChange(field, value) {
    setOverrides(prev => ({ ...prev, [field]: value }));
  }

  React.useEffect(() => {
    if (initialVariant) {
      setCurrentVariant(initialVariant);
      setOverrides({});
    }
    if (window.__BLOG_INITIAL_VARIANT_LIST) {
      setVariants(window.__BLOG_INITIAL_VARIANT_LIST || []);
    }
  }, [initialVariant]);

  useEffect(() => {
    const v = window.__BLOG_INITIAL_VARIANT_LIST;
    if (v) setVariants(v);
  }, []);

  const merged = currentVariant ? { ...currentVariant, ...overrides } : null;

  return (
    <div style={{ display: 'flex', gap: 24, width: '100%' }}>
      {/* Blog selector column (left) */}
      <div style={{ minWidth: 320, maxWidth: 340, flex: '0 0 340px' }}>
        <Card style={{ marginBottom: 16, background: '#181c24', color: '#fff' }}>
          <CardHeader>
            <CardTitle>Blog → Creative</CardTitle>
          </CardHeader>
          <CardContent>
            <BlogPostSelector selectedBlog={selectedBlog} onApply={handleApply} onGenerated={(out) => { setVariants(out || []); if (out && out.length) { setCurrentVariant(out[0]); } }} />
          </CardContent>
        </Card>
      </div>

      {/* Center column: canvas (top) + variant editor (bottom) to avoid overlap */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 24, minWidth: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Card style={{ width: 420, background: '#23263a', color: '#fff' }}>
            <CardContent>
              <BlogCreativeCanvas variant={merged} onChange={handleFieldChange} canvasId="blog-creative-canvas" />
            </CardContent>
          </Card>
        </div>

        {/* AI caption generator */}
        <AICaptionPanel
          type="blog"
          data={{ title: selectedBlog?.title, excerpt: selectedBlog?.excerpt || selectedBlog?.description, url: selectedBlog?.url }}
          onApply={(caption) => handleFieldChange('message', caption)}
        />

        {/* Variant editor stacked below the preview */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <aside style={{ width: 360 }}>
            <Card>
              <CardHeader>
                <CardTitle>Variant editor</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'grid', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Variants</label>
                    <Select value={currentVariant ? JSON.stringify(currentVariant) : ''} onValueChange={(v) => {
                      try { const parsed = JSON.parse(v); setCurrentVariant(parsed); } catch(e){}
                    }}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={variants.length ? 'Choose variant' : 'No variants available'} />
                      </SelectTrigger>
                      <SelectContent>
                        {variants.map((v, i) => (
                          <SelectItem key={i} value={JSON.stringify(v)}>{v.title || `Variant ${i+1}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label style={{ fontSize: 12 }}>Title</label>
                    <Input value={merged?.title || ''} onChange={e => handleFieldChange('title', e.target.value)} />
                  </div>

                  <div>
                    <label style={{ fontSize: 12 }}>Message</label>
                    <Textarea value={merged?.message || ''} onChange={e => handleFieldChange('message', e.target.value)} rows={4} />
                  </div>

                  <div>
                    <label style={{ fontSize: 12 }}>CTA</label>
                    <Input value={merged?.cta || ''} onChange={e => handleFieldChange('cta', e.target.value)} />
                  </div>

                  <div>
                    <label style={{ fontSize: 12 }}>Hashtags (space-separated)</label>
                    <Input value={(merged?.hashtags || []).join(' ')} onChange={e => handleFieldChange('hashtags', e.target.value.split(' ').filter(Boolean))} />
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button onClick={() => navigator.clipboard?.writeText(`${merged?.title || ''}\n\n${merged?.message || ''}\n\n${merged?.cta || ''}\n\n${(merged?.hashtags||[]).join(' ')}`)}>Copy text</Button>
                    <Button onClick={handleExport} disabled={isExporting}>
                      {isExporting ? 'Exporting...' : 'Export'}
                    </Button>
                  </div>

                  <hr />

                  {/* Local LLM controls were moved to the header to avoid duplication */}
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
}
